/**
 * Optimized Animation Patterns for 60fps Performance
 *
 * Key optimizations:
 * - Uses CSS transforms (translate3d, scale, rotate) instead of position changes
 * - Adds will-change hints for GPU acceleration
 * - Reduces animation complexity on mobile devices
 * - Uses transform and opacity exclusively (avoid layout thrashing)
 * - Implements reduced motion preferences
 */

// Detect if device is mobile (for reduced animation complexity)
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

// Detect if user prefers reduced motion
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Should use reduced animations
export const shouldReduceMotion = () => isMobile() || prefersReducedMotion();

/**
 * Fade in animation - GPU accelerated, no layout thrashing
 */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: shouldReduceMotion() ? 0.2 : 0.6 }
};

/**
 * Fade in with slide up - Uses transform instead of y position
 * Better performance than animating 'y' property
 */
export const fadeInUp = {
  initial: { opacity: 0, transform: 'translateY(20px) translateZ(0)' },
  animate: { opacity: 1, transform: 'translateY(0px) translateZ(0)' },
  exit: { opacity: 0, transform: 'translateY(20px) translateZ(0)' },
  transition: { duration: shouldReduceMotion() ? 0.2 : 0.6 }
};

/**
 * Fade in with slide down - Uses transform
 */
export const fadeInDown = {
  initial: { opacity: 0, transform: 'translateY(-20px) translateZ(0)' },
  animate: { opacity: 1, transform: 'translateY(0px) translateZ(0)' },
  exit: { opacity: 0, transform: 'translateY(-20px) translateZ(0)' },
  transition: { duration: shouldReduceMotion() ? 0.2 : 0.6 }
};

/**
 * Scale in - GPU accelerated
 */
export const scaleIn = {
  initial: { opacity: 0, transform: 'scale(0.9) translateZ(0)' },
  animate: { opacity: 1, transform: 'scale(1) translateZ(0)' },
  exit: { opacity: 0, transform: 'scale(0.9) translateZ(0)' },
  transition: { duration: shouldReduceMotion() ? 0.15 : 0.3 }
};

/**
 * Hover scale - Optimized for buttons and cards
 * Reduced scale amount on mobile for better performance
 */
export const hoverScale = {
  whileHover: { scale: shouldReduceMotion() ? 1.01 : 1.02 },
  whileTap: { scale: shouldReduceMotion() ? 0.99 : 0.98 },
  transition: { duration: 0.2 }
};

/**
 * Hover lift - Uses translate3d for GPU acceleration
 * Combines transform properties for better performance
 */
export const hoverLift = {
  whileHover: {
    transform: 'translateY(-4px) translateZ(0) scale(1.02)',
    transition: { duration: 0.2 }
  },
  whileTap: {
    transform: 'translateY(0px) translateZ(0) scale(0.98)',
    transition: { duration: 0.1 }
  }
};

/**
 * Stagger children - For list animations
 * Optimized duration based on device
 */
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: shouldReduceMotion() ? 0.05 : 0.1
    }
  }
};

/**
 * Stagger item - Individual list item animation
 */
export const staggerItem = {
  initial: { opacity: 0, transform: 'translateY(10px) translateZ(0)' },
  animate: { opacity: 1, transform: 'translateY(0px) translateZ(0)' },
  transition: { duration: shouldReduceMotion() ? 0.2 : 0.4 }
};

/**
 * Will change utility - Adds GPU acceleration hint
 * Use sparingly - only on elements that actually animate
 */
export const getWillChangeStyle = (properties: string[]) => {
  if (shouldReduceMotion()) return {};
  return { willChange: properties.join(', ') as any };
};

/**
 * Viewport animation config - Optimized for scroll-triggered animations
 */
export const viewportConfig = {
  once: true,
  amount: 0.3 // Trigger when 30% of element is visible
};

/**
 * Slide in from left - Uses transform
 */
export const slideInLeft = {
  initial: { opacity: 0, transform: 'translateX(-20px) translateZ(0)' },
  animate: { opacity: 1, transform: 'translateX(0px) translateZ(0)' },
  transition: { duration: shouldReduceMotion() ? 0.2 : 0.5 }
};

/**
 * Slide in from right - Uses transform
 */
export const slideInRight = {
  initial: { opacity: 0, transform: 'translateX(20px) translateZ(0)' },
  animate: { opacity: 1, transform: 'translateX(0px) translateZ(0)' },
  transition: { duration: shouldReduceMotion() ? 0.2 : 0.5 }
};

/**
 * Pulse animation - Subtle breathing effect
 * Reduced intensity on mobile
 */
export const pulse = {
  animate: {
    opacity: [1, 0.8, 1],
    transform: shouldReduceMotion()
      ? 'scale(1) translateZ(0)'
      : 'scale(1.02) translateZ(0)'
  },
  transition: {
    duration: shouldReduceMotion() ? 2 : 3,
    repeat: Infinity,
    ease: 'easeInOut'
  }
};

/**
 * Shimmer/skeleton loading animation
 * Optimized for performance
 */
export const shimmer = {
  animate: {
    backgroundPosition: ['200% 0', '-200% 0']
  },
  transition: {
    duration: 1.5,
    repeat: Infinity,
    ease: 'linear'
  }
};

/**
 * Optimized spring animations
 * Stiffer springs on mobile for quicker settling
 */
export const springConfig = {
  type: 'spring' as const,
  stiffness: shouldReduceMotion() ? 300 : 200,
  damping: shouldReduceMotion() ? 30 : 20
};

/**
 * Delay config - Reduced delays on mobile
 */
export const getDelay = (baseDelay: number) => {
  return shouldReduceMotion() ? baseDelay * 0.5 : baseDelay;
};

/**
 * Duration config - Reduced durations on mobile
 */
export const getDuration = (baseDuration: number) => {
  return shouldReduceMotion() ? baseDuration * 0.5 : baseDuration;
};

/**
 * Combined animation config for entrance animations
 * Handles mobile optimizations automatically
 */
export const entranceAnimation = (direction: 'up' | 'down' | 'left' | 'right' | 'none' = 'up') => {
  const transforms = {
    up: 'translateY(20px)',
    down: 'translateY(-20px)',
    left: 'translateX(-20px)',
    right: 'translateX(20px)',
    none: 'translateY(0px)'
  };

  return {
    initial: {
      opacity: 0,
      transform: `${transforms[direction]} translateZ(0)`
    },
    animate: {
      opacity: 1,
      transform: `translateY(0px) translateX(0px) translateZ(0)`
    },
    transition: {
      duration: getDuration(0.6),
      ease: 'easeOut'
    }
  };
};
